import React, { useCallback } from 'react';
import { Paper, Tooltip } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { ToggleButtonGroup, ToggleButton } from '@mui/material';
import ListIcon from '@mui/icons-material/List';
import TodayIcon from '@mui/icons-material/Today';
import isNil from 'lodash/fp/isNil';
import { DateTime } from 'luxon';
import { useFeatureFlag } from 'modules/common/hooks';
import { useMessages } from 'modules/intl';
import {
  useNotesFilterContext,
  NOTES_PAGE_TYPE
} from './NotesFilterContextProvider';
import { useNotesSelectionReset } from './list/hooks';
import { DEFAULT_PERIOD } from './Timeline';

const useStyles = makeStyles((theme) => ({
  paper: {
    display: 'flex',
    border: `1px solid ${theme.palette.divider}`,
    flexWrap: 'wrap'
  },
  divider: {
    margin: theme.spacing(1, 0.5)
  }
}));

const useButtonGroupStyles = makeStyles((theme) => ({
  grouped: {
    margin: '3px',
    padding: theme.spacing(0.5),
    border: 'none',
    '&:not(:first-child)': {
      borderRadius: theme.shape.borderRadius
    },
    '&:first-child': {
      borderRadius: theme.shape.borderRadius
    }
  }
}));

const messageIds = ['myWorkViewSwitcher.timeline', 'myWorkViewSwitcher.list'];

export const NotesPageTabs: React.FC = () => {
  const classes = useStyles();
  const buttonGroupClasses = useButtonGroupStyles();
  const {
    filterContext: { pageType, searchCriteria, ...rest },
    setFilterContext
  } = useNotesFilterContext();
  const { clearForm } = useNotesSelectionReset();
  const handleViewType = useCallback(
    (event, newViewType) => {
      if (!isNil(newViewType) && pageType !== newViewType) {
        clearForm();
        const disallowedProperties = ['dateRange:'];
        const allKeys = Object.keys(searchCriteria.criterions);
        const newCriterias = allKeys.reduce((next, key) => {
          if (!disallowedProperties.includes(key)) {
            return { ...next, [key]: searchCriteria.criterions[key] };
          } else {
            return next;
          }
        }, {});
        const searchCriteriaObj = {
          ...searchCriteria,
          ...{ criterions: newCriterias }
        };
        setFilterContext({
          searchCriteria: searchCriteriaObj,
          ...rest,
          selectedDateRange: {
            start: DateTime.local(),
            end: DateTime.local()
          },
          pageType: newViewType,
          timelinePeriod: DEFAULT_PERIOD
        });
      }
    },
    [pageType, setFilterContext, clearForm, searchCriteria, rest]
  );
  const isTiwScreenReaderEnabled = useFeatureFlag('isTiwScreenReaderEnabled');

  const messages = useMessages(messageIds);
  const timelineTitle = messages['myWorkViewSwitcher.timeline'];
  const listTitle = messages['myWorkViewSwitcher.list'];

  return (
    <Paper elevation={0} className={classes.paper}>
      <ToggleButtonGroup
        classes={buttonGroupClasses}
        size="small"
        value={pageType}
        exclusive
        onChange={handleViewType}
      >
        <ToggleButton
          value={NOTES_PAGE_TYPE.TIMELINE}
          data-testid={'timeline-page-button'}
          aria-label={isTiwScreenReaderEnabled ? timelineTitle : undefined}
        >
          <Tooltip title={timelineTitle}>
            <TodayIcon />
          </Tooltip>
        </ToggleButton>
        <ToggleButton
          value={NOTES_PAGE_TYPE.LIST}
          data-testid={'list-page-button'}
          aria-label={isTiwScreenReaderEnabled ? listTitle : undefined}
        >
          <Tooltip title={listTitle}>
            <ListIcon />
          </Tooltip>
        </ToggleButton>
      </ToggleButtonGroup>
    </Paper>
  );
};
export default React.memo(NotesPageTabs);
